import { Platform } from 'react-native';
import { supabase } from './supabase';
import { getCurrentJourney, inferAuthModeFromStorage } from './funnelJourney';
import { deferSupabaseAuthCallbackWork } from './deferSupabaseAuthCallbackWork';
import { sanitizeSensitiveUrl } from './sensitiveUrl';
import type { Json } from './database.client.types';

interface QueuedFunnelEvent {
    id: string;
    event_name: string;
    properties: Record<string, unknown>;
    page_url?: string;
    client_timestamp: string;
}

interface TrackerConfig {
    enabled: boolean;
    flushIntervalMs: number;
    flushBatchSize: number;
    maxPersistedEvents: number;
    debug: boolean;
}

const DEFAULT_CONFIG: TrackerConfig = {
    enabled: true,
    flushIntervalMs: 3000,
    flushBatchSize: 25,
    maxPersistedEvents: 400,
    debug: __DEV__,
};

const TRACKER_QUEUE_KEY = 'pz_funnel_queue_v2';
const TRACKER_SESSION_ID_KEY = 'funnel_session_id';

function createEventId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return template.replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

class FunnelTracker {
    private queue: QueuedFunnelEvent[] = [];
    private sessionId: string | null = null;
    private flushInterval: ReturnType<typeof setInterval> | null = null;
    private authSubscription: { unsubscribe: () => void } | null = null;
    private config: TrackerConfig;
    private isInitialized = false;
    private isFlushing = false;
    private pendingFlushFailureSignal = false;
    private pendingDroppedEvents = 0;

    constructor(config: Partial<TrackerConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };

        if (Platform.OS === 'web') {
            this.initialize();
        }
    }

    private log(...args: unknown[]) {
        if (this.config.debug) {
            console.log('[FunnelTrackerV2]', ...args);
        }
    }

    private isWebRuntime(): boolean {
        return Platform.OS === 'web' && typeof window !== 'undefined';
    }

    private initialize() {
        if (this.isInitialized || !this.isWebRuntime()) {
            return;
        }

        this.queue = this.readQueueFromStorage();
        this.sessionId = this.getOrCreateSessionId();
        this.startAutoFlush();
        this.setupVisibilityFlush();
        this.setupAuthFlush();
        this.isInitialized = true;

        this.log('Initialized', {
            sessionId: this.sessionId,
            queuedEvents: this.queue.length,
        });
    }

    private getCurrentPageUrl(): string | undefined {
        if (!this.isWebRuntime()) {
            return undefined;
        }

        return sanitizeSensitiveUrl(window.location.href);
    }

    private getOrCreateSessionId(): string {
        if (!this.isWebRuntime()) {
            return 'non-web';
        }

        const existing = window.sessionStorage.getItem(TRACKER_SESSION_ID_KEY);
        if (existing) {
            return existing;
        }

        const created = createEventId();
        window.sessionStorage.setItem(TRACKER_SESSION_ID_KEY, created);
        return created;
    }

    private readQueueFromStorage(): QueuedFunnelEvent[] {
        if (!this.isWebRuntime()) {
            return [];
        }

        try {
            const raw = window.sessionStorage.getItem(TRACKER_QUEUE_KEY);
            if (!raw) {
                return [];
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
                return [];
            }

            return parsed.filter((event): event is QueuedFunnelEvent => {
                return Boolean(event && event.id && event.event_name && event.client_timestamp);
            });
        } catch {
            return [];
        }
    }

    private persistQueue() {
        if (!this.isWebRuntime()) {
            return;
        }

        window.sessionStorage.setItem(TRACKER_QUEUE_KEY, JSON.stringify(this.queue));
    }

    private startAutoFlush() {
        if (!this.isWebRuntime()) {
            return;
        }

        this.flushInterval = setInterval(() => {
            this.flush();
        }, this.config.flushIntervalMs);
    }

    private setupVisibilityFlush() {
        if (!this.isWebRuntime()) {
            return;
        }

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.flush(true);
            }
        });

        window.addEventListener('beforeunload', () => {
            this.flush(true);
        });
    }

    private setupAuthFlush() {
        // Supabase auth callbacks must stay synchronous; defer follow-up client work.
        this.authSubscription = supabase.auth.onAuthStateChange(() => {
            deferSupabaseAuthCallbackWork('funnel tracker flush', () => this.flush());
        }).data.subscription;
    }

    private enqueueInternal(eventName: string, properties: Record<string, unknown>, pageUrl?: string): string {
        const event: QueuedFunnelEvent = {
            id: createEventId(),
            event_name: eventName,
            properties,
            page_url: sanitizeSensitiveUrl(pageUrl || this.getCurrentPageUrl()),
            client_timestamp: new Date().toISOString(),
        };

        this.queue.push(event);

        if (this.queue.length > this.config.maxPersistedEvents) {
            const overflow = this.queue.length - this.config.maxPersistedEvents;
            this.queue.splice(0, overflow);
            this.pendingDroppedEvents += overflow;
            this.pendingFlushFailureSignal = true;
        }

        this.persistQueue();
        return event.id;
    }

    private maybeQueueCollectorSignal() {
        if (!this.pendingFlushFailureSignal && this.pendingDroppedEvents === 0) {
            return;
        }

        this.pendingFlushFailureSignal = false;
        const droppedEvents = this.pendingDroppedEvents;
        this.pendingDroppedEvents = 0;
        const journey = getCurrentJourney();
        const authMode = journey?.authModeCurrent || inferAuthModeFromStorage();
        const fallbackJourneyId = `collector-${this.sessionId || createEventId()}`;

        this.enqueueInternal('collector_flush_failed', {
            schema_version: '2',
            journey_id: journey?.journeyId || fallbackJourneyId,
            journey_seq: journey?.journeySeq || 0,
            auth_mode_start: journey?.authModeStart || authMode,
            auth_mode_event: authMode,
            scenario_candidate: journey?.scenarioCandidate || 'unknown',
            scenario_final: journey?.scenarioFinal || null,
            entry_surface: journey?.entrySurface || 'collector',
            entry_path: journey?.entryPath || '/collector',
            chat_session_id: journey?.chatSessionId || null,
            project_id: journey?.projectId || null,
            reason: droppedEvents > 0 ? 'buffer_overflow_or_flush_failure' : 'flush_failure',
            dropped_events: droppedEvents,
        });
    }

    track(eventName: string, properties: Record<string, unknown> = {}): string | null {
        if (!this.config.enabled) {
            return null;
        }

        if (!this.isInitialized) {
            this.initialize();
        }

        const eventId = this.enqueueInternal(eventName, properties);

        const shouldFlushNow = this.queue.length >= this.config.flushBatchSize
            || eventName === 'journey_finished'
            || eventName === 'auth_handoff_result'
            || eventName === 'delivery_save_outcome';

        if (shouldFlushNow) {
            this.flush();
        }

        return eventId;
    }

    async flush(sync = false) {
        if (!this.config.enabled) {
            return;
        }

        const sessionId = this.sessionId;
        if (this.isFlushing || this.queue.length === 0 || !sessionId) {
            return;
        }

        this.isFlushing = true;

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user?.id) {
                return;
            }

            this.maybeQueueCollectorSignal();

            const batch = this.queue.slice(0, this.config.flushBatchSize);
            this.queue = this.queue.slice(this.config.flushBatchSize);
            this.persistQueue();

            const payload = batch.map((event) => ({
                id: event.id,
                user_id: session.user.id,
                session_id: sessionId,
                project_id: typeof event.properties.project_id === 'string' ? event.properties.project_id : null,
                event_name: event.event_name,
                // Match the JSON wire format (including omission of undefined values).
                properties: JSON.parse(JSON.stringify(event.properties)) as Json,
                page_url: event.page_url,
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                client_timestamp: event.client_timestamp,
            }));

            const { error } = await supabase
                .from('funnel_events')
                .upsert(payload, {
                    onConflict: 'id',
                    ignoreDuplicates: true,
                });

            if (error) {
                this.queue = [...batch, ...this.queue];
                this.persistQueue();
                this.pendingFlushFailureSignal = true;
                this.log('Flush failed', error.message);
                return;
            }

            this.log('Flushed events', {
                count: batch.length,
                sync,
                remaining: this.queue.length,
            });

            if (this.queue.length > 0) {
                setTimeout(() => this.flush(sync), 0);
            }
        } catch (error) {
            this.pendingFlushFailureSignal = true;
            this.log('Flush threw', error);
        } finally {
            this.isFlushing = false;
        }
    }

    disable() {
        this.config.enabled = false;
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }
        this.authSubscription?.unsubscribe();
        this.authSubscription = null;
    }

    enable() {
        this.config.enabled = true;
        if (!this.isInitialized) {
            this.initialize();
        }

        if (!this.flushInterval) {
            this.startAutoFlush();
        }
    }

    getSessionId(): string | null {
        return this.sessionId;
    }

    getBufferSize(): number {
        return this.queue.length;
    }
}

export const funnelTracker = new FunnelTracker();
export { FunnelTracker };
